import {
    AddToCartPriceStyled,
    AddToCartSavedAmountStyled,
    ImageCellStyled,
    ImageWrapperStyled,
    InfoCellStyled,
    ItemCouponStyled,
    ItemPriceCellStyled,
    MobileItemPriceStyled,
    RemoveButtonCellStyled,
    TotalPriceCellStyled,
} from './Item.style';
import ItemInfo from './ItemInfo';
import Image from 'components/Basic/Image/Image';
import RemoveCartItemButton from 'components/Basic/RemoveCartItemButton/RemoveCartItemButton';
import { WarningBar } from 'components/Basic/WarningBar/WarningBar';
import Spinbox from 'components/Forms/Spinbox/Spinbox';
import { isB2bDomain } from 'helpers/isDomainLocale';
import { useAddToCart } from 'hooks/cart/useAddToCart';
import { getSellingPriceByDomain, useFormatPrice } from 'hooks/formatting/useFormatPrice';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { useDomainConfig } from 'hooks/useDomainConfig';
import { useCurrentUserData } from 'hooks/user/useCurrentUserData';
import { TescomaClubIcon } from 'public/svg/IconsSvg';
import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import { twJoin } from 'tailwind-merge';
import { CartItemType } from 'types/cart';

type ItemProps = {
    idSuffix?: string;
    item: CartItemType;
    listIndex: number;
    onItemRemove?: () => void;
    isAddToCartFetchingOnAnyProduct: boolean;
    setIsAddToCartFetchingOnAnyProduct: Dispatch<SetStateAction<boolean>>;
};

const Item: FC<ItemProps> = ({
    idSuffix,
    item,
    listIndex,
    onItemRemove,
    setIsAddToCartFetchingOnAnyProduct,
    isAddToCartFetchingOnAnyProduct,
}) => {
    const testIdentifier = 'pages-cart-list-item-';
    const itemCatnum = item.product.catalogNumber;
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const spinboxRef = useRef<HTMLInputElement>(null);
    const t = useTypedTranslationFunction();
    const { formatPriceNumber, formatPriceObject } = useFormatPrice();
    const domainConfig = useDomainConfig();
    const [changeCartItemQuantity, addToCartFetching] = useAddToCart('cart');
    const isSpecialPrice =
        getSellingPriceByDomain(item.product.price, domainConfig) <
        getSellingPriceByDomain(item.product.prices.basic, domainConfig);
    const { isUserLoggedIn } = useCurrentUserData();
    const promoCode = item.product.promoCode;
    const itemPrice = formatPriceObject(item.product.price) + '\u00A0/\u00A0' + t('pc');
    const shouldShowTescomaClubIcon = !!(isUserLoggedIn && item.product.prices.tescoma);

    useEffect(() => {
        setIsAddToCartFetchingOnAnyProduct(addToCartFetching);
    }, [addToCartFetching, setIsAddToCartFetchingOnAnyProduct]);

    const onChangeValueHandler = (spinboxValue: number) => {
        if (timeoutRef.current === null) {
            timeoutRef.current = setUpdateTimeout(spinboxValue);
        } else {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setUpdateTimeout(spinboxValue);
        }
    };

    const setUpdateTimeout = (spinboxValue: number) => {
        return setTimeout(() => {
            changeCartItemQuantity(item.product.uuid, listIndex, spinboxValue, 'cart', true);
        }, 500);
    };

    return (
        <div
            className={twJoin(
                'relative flex flex-row flex-wrap items-center border-b border-grayLighter last:border-b-0',
                !isB2bDomain(domainConfig) ? 'py-[55px] pb-[20px] xl:py-[20px]' : 'py-1',
            )}
            data-testid={testIdentifier + itemCatnum}
        >
            {!isB2bDomain(domainConfig) && (
                <ImageCellStyled data-testid={testIdentifier + 'image'}>
                    <ImageWrapperStyled type="product" href={item.product.slug}>
                        <Image src={item.product.image} width="60" height="80" alt={item.product.fullName} />
                    </ImageWrapperStyled>
                </ImageCellStyled>
            )}
            <InfoCellStyled>
                <ItemInfo item={item} fetching={addToCartFetching} className="max-w-[230px]" />
            </InfoCellStyled>
            <div
                className={twJoin('mr-4 flex w-[125px] items-center', isB2bDomain(domainConfig) && 'py-1')}
                data-testid={testIdentifier + 'spinbox'}
            >
                <Spinbox
                    id={item.product.uuid}
                    idSuffix={idSuffix}
                    size={isB2bDomain(domainConfig) ? 'small' : 'default'}
                    min={item.product.b2bMinimumProductOrder ?? 1}
                    max={item.product.maximumOrderableQuantity}
                    step={item.product.b2bMinimumProductOrder ?? 1}
                    multiple={item.product.b2bMinimumProductOrder ?? 1}
                    overstepMessage={{
                        tooHigh: t('You cannot add more items'),
                        onlyMultiplesOf: t('Only multiples of {{ multiple }}', {
                            multiple: item.product.b2bMinimumProductOrder ?? 1,
                        }),
                    }}
                    defaultValue={item.quantity}
                    ref={spinboxRef}
                    disabled={isAddToCartFetchingOnAnyProduct}
                    onChangeValueCallback={onChangeValueHandler}
                />
            </div>

            <ItemPriceCellStyled data-testid={testIdentifier + 'itemprice'}>{itemPrice}</ItemPriceCellStyled>
            <TotalPriceCellStyled data-testid={testIdentifier + 'totalprice'}>
                <MobileItemPriceStyled>{itemPrice}</MobileItemPriceStyled>
                {!addToCartFetching ? (
                    <>
                        <div>
                            {isB2bDomain(domainConfig) ? (
                                <div className={twJoin(isSpecialPrice ? 'text-red' : 'text-primary', 'font-bold')}>
                                    {formatPriceNumber(
                                        getSellingPriceByDomain(item.product.price, domainConfig) * item.quantity,
                                    )}
                                </div>
                            ) : (
                                <>
                                    <AddToCartPriceStyled
                                        isOldPrice={isSpecialPrice}
                                        data-testid={testIdentifier + '-price'}
                                    >
                                        {formatPriceNumber(
                                            getSellingPriceByDomain(item.product.prices.basic, domainConfig) *
                                                item.quantity,
                                        )}
                                    </AddToCartPriceStyled>
                                    {isSpecialPrice && (
                                        <>
                                            <AddToCartSavedAmountStyled>
                                                {`${t('You will save')} `}
                                                {formatPriceNumber(
                                                    getSellingPriceByDomain(item.product.prices.basic, domainConfig) *
                                                        item.quantity -
                                                        getSellingPriceByDomain(item.product.price, domainConfig) *
                                                            item.quantity,
                                                )}
                                            </AddToCartSavedAmountStyled>
                                            <AddToCartPriceStyled
                                                isSpecialPrice
                                                data-testid={testIdentifier + '-price-special'}
                                            >
                                                {formatPriceNumber(
                                                    getSellingPriceByDomain(item.product.price, domainConfig) *
                                                        item.quantity,
                                                )}
                                                {shouldShowTescomaClubIcon && (
                                                    <TescomaClubIcon className="-mt-1 ml-1 h-4 w-4 text-red" />
                                                )}
                                            </AddToCartPriceStyled>
                                        </>
                                    )}
                                </>
                            )}

                            {promoCode !== null && (
                                <ItemCouponStyled>
                                    {promoCode.isGiftVoucher ? t('Gift voucher') : t('Discount code')} {promoCode.code}
                                </ItemCouponStyled>
                            )}
                        </div>
                    </>
                ) : (
                    <Skeleton className="h-5 w-[125px]" />
                )}
            </TotalPriceCellStyled>
            <RemoveButtonCellStyled>
                <RemoveCartItemButton
                    onItemRemove={onItemRemove}
                    data-testid="pages-cart-removecartitembutton"
                    productName={item.product.fullName}
                />
            </RemoveButtonCellStyled>
            {item.product.isExcludedFromDelivery && (
                <WarningBar className="mt-4">
                    {t(
                        'This product has shipping restrictions, so some shipping options in the next step may be limited.',
                    )}
                </WarningBar>
            )}
        </div>
    );
};

export default Item;
